import { useMemo, type ReactNode } from "react";
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { ArrivalBook, LearnerViewInPlay } from "@pushback-and-chaos/contracts";
import { ProblemJsonError, type PushbackClient } from "@pushback-and-chaos/client";
import { ARRIVAL } from "../design/copy.js";
import { apiClient } from "../data/transport.js";
import { toProse } from "../data/adapt.js";
import { PlaySurface, type PlayBook, type PlayPrologue } from "./play.js";
import { arrivalPages } from "./storyteller/arrival-atoms.js";
import { bookDate } from "./storyteller/casebook-atoms.js";

/**
 * THE ARRIVAL BOOK (2026-10-11, Andre): how a learner and AJ met, read back
 * as the record's first book. The play surface itself, closed, handed the
 * meeting as a book: a title page with the learner's own name and the day
 * they met, then the four chapters. There is no prologue, no tape and no
 * case to try again; the one door leads back to the record.
 *
 * The book exists only while its intake does. The server answers 404 before
 * the read-back is confirmed and again once the coach's data is removed, and
 * that is an answer, not a fault: the page says there is no book, and the
 * record's row stays away.
 */

/**
 * The one query for the book, shared by the record's row and the book itself,
 * so opening the book from the row reads what the row already fetched. Never
 * retried: a refusal is the answer.
 */
export function arrivalBookQuery(client: PushbackClient) {
  return queryOptions({
    queryKey: ["arrival"],
    queryFn: () => client.getArrivalBook(),
    staleTime: 60_000,
    retry: false,
  });
}

/** The server's "there is no book" (404), as opposed to a fault. */
export function isNoArrivalBook(error: unknown): boolean {
  return error instanceof ProblemJsonError && error.status === 404;
}

export function ArrivalBookScreen({ onBack }: { readonly onBack: () => void }): ReactNode {
  const book = useQuery(arrivalBookQuery(apiClient()));

  if (book.isError) {
    return (
      <div className="pcs-book-status" data-testid="arrival-status">
        <p className="m-0">{isNoArrivalBook(book.error) ? ARRIVAL.none : ARRIVAL.failed}</p>
        <button type="button" className="pcs-book-back mt-3" onClick={onBack}>{ARRIVAL.back}</button>
      </div>
    );
  }
  if (book.data === undefined) return <p className="pcs-book-status">{ARRIVAL.loading}</p>;
  return <OpenArrivalBook book={book.data} onBack={onBack} />;
}

function OpenArrivalBook({ book, onBack }: { readonly book: ArrivalBook; readonly onBack: () => void }): ReactNode {
  const pages = useMemo(() => arrivalPages(book), [book]);
  const playBook: PlayBook = useMemo(
    () => ({
      kind: "arrival",
      items: () => pages.items,
      chapters: () => pages.chapters,
      reviewKey: pages.reviewKey,
      sceneFor: pages.sceneFor,
      documentsFor: pages.documentsFor,
      allDocuments: () => pages.allDocuments,
      titleNote: bookDate(book.metAt),
      onBack,
    }),
    [pages, book, onBack],
  );
  // The title page is the book's own: its name where a case's client goes,
  // the learner's name where the industry goes, no question, no prologue.
  const prologue: PlayPrologue = useMemo(
    () => ({ slug: "arrival", client: ARRIVAL.title, industry: book.readBack.name, question: "", pages: [] }),
    [book.readBack.name],
  );
  const view: LearnerViewInPlay = useMemo(
    () => ({
      engagement_question: toProse(""),
      narration: [],
      documents: [],
      facts: [],
      npcs: [],
      open_threads: [],
      hypotheses_on_record: [],
      ruling_ledger: [],
      situation_pressure: "CALM",
      current_beat: { id: "arrival", title: toProse("") },
    }),
    [],
  );

  return (
    <PlaySurface
      sessionId="arrival"
      view={view}
      thread={[]}
      pendingText={null}
      hypotheses={[]}
      notes={{ established: [], open: [] }}
      npcTells={[]}
      statuses={[]}
      connection="live"
      resumedFromTurn={null}
      busy={false}
      failure={null}
      blockedReason={null}
      canRecommend={false}
      orientationNoteDocId={null}
      mentorAsk={null}
      composerPlaceholder=""
      environment={null}
      environmentPlan={null}
      dictate={null}
      narrationAudio={null}
      readAloud={false}
      prologue={prologue}
      book={playBook}
      submit={noop}
      retry={noop}
      openSettings={noop}
      openRecommend={noop}
    />
  );
}

function noop(): void {
  /* a closed book takes no turns */
}
